import React from 'react';
import {
  confirmDiscardUnsavedChanges,
  hasUnsavedChanges,
} from '../utils/unsavedChanges';

export const CHUNK_RELOAD_ATTEMPT_KEY = 'recon.chunk-reload-attempt.v1';
export const CHUNK_RELOAD_WINDOW_MS = 5 * 60 * 1000;

const CHUNK_LOAD_ERROR_PATTERN = /(?:ChunkLoadError|Loading (?:CSS )?chunk [^\s]+ failed|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module)/i;

export const isChunkLoadError = error => {
  const name = String(error?.name || '');
  const message = String(error?.message || error || '');
  return CHUNK_LOAD_ERROR_PATTERN.test(`${name} ${message}`);
};

const browserSessionStorage = () => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const writeChunkReloadAttempt = (storage, timestamp) => {
  if (!storage) return false;
  try {
    storage.setItem(CHUNK_RELOAD_ATTEMPT_KEY, String(timestamp));
    return true;
  } catch {
    return false;
  }
};

export const claimChunkReloadAttempt = ({
  storage = browserSessionStorage(),
  now = Date.now(),
  windowMs = CHUNK_RELOAD_WINDOW_MS,
} = {}) => {
  if (!storage) return { claimed: false, reason: 'storage-unavailable' };

  try {
    const previousAttempt = Number(storage.getItem(CHUNK_RELOAD_ATTEMPT_KEY));
    const elapsed = Number(now) - previousAttempt;
    if (
      Number.isFinite(previousAttempt) &&
      previousAttempt > 0 &&
      elapsed >= 0 &&
      elapsed < windowMs
    ) {
      return { claimed: false, reason: 'already-attempted' };
    }

    if (!writeChunkReloadAttempt(storage, Number(now))) {
      return { claimed: false, reason: 'storage-unavailable' };
    }
    return { claimed: true, reason: 'first-attempt' };
  } catch {
    return { claimed: false, reason: 'storage-unavailable' };
  }
};

const defaultReloadApplication = () => window.location.reload();

class ApplicationErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      error: null,
      recoveryStatus: '',
    };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    if (!isChunkLoadError(error)) return;

    if (hasUnsavedChanges()) {
      this.setState({ recoveryStatus: 'unsaved-work' });
      return;
    }

    const recovery = claimChunkReloadAttempt({
      storage: this.getStorage(),
      now: this.getNow(),
      windowMs: this.props.reloadWindowMs || CHUNK_RELOAD_WINDOW_MS,
    });
    if (!recovery.claimed) {
      this.setState({ recoveryStatus: recovery.reason });
      return;
    }

    this.reloadApplication();
  }

  getNow = () => {
    const value = typeof this.props.now === 'function'
      ? this.props.now()
      : Date.now();
    return Number.isFinite(Number(value)) ? Number(value) : Date.now();
  };

  getStorage = () => (
    Object.prototype.hasOwnProperty.call(this.props, 'storage')
      ? this.props.storage
      : browserSessionStorage()
  );

  reloadApplication = () => {
    const reload = this.props.reloadApplication || defaultReloadApplication;
    reload();
  };

  handleReload = () => {
    if (!confirmDiscardUnsavedChanges()) return;

    if (isChunkLoadError(this.state.error)) {
      writeChunkReloadAttempt(this.getStorage(), this.getNow());
    }
    this.reloadApplication();
  };

  render() {
    const { error, recoveryStatus } = this.state;
    if (!error) return this.props.children;

    const chunkFailure = isChunkLoadError(error);
    const unsavedWork = recoveryStatus === 'unsaved-work';
    const recoveryUnavailable = recoveryStatus === 'storage-unavailable';

    return (
      <main
        role="alert"
        aria-live="assertive"
        style={{
          minHeight: '100vh',
          boxSizing: 'border-box',
          display: 'grid',
          placeItems: 'center',
          padding: '24px',
          background: '#f4f7f8',
          color: '#263238',
          fontFamily: 'sans-serif',
        }}
      >
        <section
          style={{
            width: 'min(560px, 100%)',
            boxSizing: 'border-box',
            padding: '28px',
            border: '1px solid #d5dfe1',
            borderRadius: '8px',
            background: '#fff',
            boxShadow: '0 2px 8px rgba(38, 50, 56, 0.08)',
          }}
        >
          <h1 style={{ margin: 0, fontSize: '24px' }}>
            {chunkFailure
              ? 'Application update could not be loaded'
              : 'Application screen could not be displayed'}
          </h1>
          <p style={{ margin: '12px 0 0', lineHeight: 1.5 }}>
            {chunkFailure
              ? 'The application files changed or were temporarily unavailable. Reload to retrieve the current version.'
              : 'An unexpected display error occurred. Reload the application to try again.'}
          </p>
          {recoveryStatus === 'already-attempted' && (
            <p style={{ margin: '12px 0 0', lineHeight: 1.5 }}>
              Automatic recovery was already attempted. The application will remain on this page until you choose to reload.
            </p>
          )}
          {unsavedWork && (
            <p style={{ margin: '12px 0 0', lineHeight: 1.5, color: '#b45309' }}>
              Unsaved work was detected, so the application did not reload automatically. Reloading may discard that work.
            </p>
          )}
          {recoveryUnavailable && (
            <p style={{ margin: '12px 0 0', lineHeight: 1.5 }}>
              Automatic recovery is unavailable in this browser session.
            </p>
          )}
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              marginTop: '20px',
              padding: '10px 18px',
              border: 0,
              borderRadius: '4px',
              background: '#00695c',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: 700,
            }}
          >
            Reload Application
          </button>
        </section>
      </main>
    );
  }
}

export default ApplicationErrorBoundary;
